/**
 * ProfessionalCard — one card in the /marketplace listing.
 *
 * Renders the name verbatim (including "Demo:", "Sample Profile:",
 * "Demo Organization:" prefixes). Demo cards get a Demo badge in the
 * top-right corner + a subtle accent border so users can instantly
 * tell them apart from real listings.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Briefcase } from "lucide-react";
import DemoBadge from "./DemoBadge";

const BIO_TRUNCATE_LEN = 140;

function truncateBio(bio) {
  if (!bio) return "";
  if (bio.length <= BIO_TRUNCATE_LEN) return bio;
  return bio.slice(0, BIO_TRUNCATE_LEN).trim() + "…";
}

function renderServiceArea(sa) {
  if (sa === "nationwide") return "Nationwide";
  if (Array.isArray(sa)) return sa.join(" · ");
  return "—";
}

export default function ProfessionalCard({ pro }) {
  const isDemo = Boolean(pro?.is_demo || pro?.demo_profile);
  const rateRange = pro?.rate_range || "$$";
  return (
    <Link
      to={`/marketplace/${pro.id}`}
      data-testid="professional-card"
      data-is-demo={isDemo ? "true" : "false"}
      aria-label={`View ${pro.name} — ${isDemo ? "demo profile" : "real professional"}`}
      className="group relative rounded-2xl block p-5 md:p-6 transition-all hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-[var(--v2-accent,#5D50C8)]/60"
      style={{
        background: "var(--v2-surface, #13131A)",
        border: isDemo
          ? "1px solid rgba(167,139,250,0.44)"
          : "1px solid var(--v2-border, rgba(255,255,255,0.08))",
        boxShadow: isDemo ? "0 0 0 1px rgba(167,139,250,0.10) inset" : undefined,
      }}
    >
      {isDemo && (
        <div className="absolute top-3 right-3 md:top-4 md:right-4">
          <DemoBadge compact />
        </div>
      )}
      <div className="pr-24 md:pr-28 space-y-2">
        <h3
          className="text-lg md:text-xl font-semibold leading-snug"
          style={{
            color: "var(--v2-text, #F5F5F0)",
            fontFamily: "'Space Grotesk', 'Inter', sans-serif",
          }}
        >
          {pro.name}
        </h3>
        <div className="flex flex-wrap gap-1.5">
          {(pro.categories || []).map((slug) => (
            <span
              key={slug}
              className="text-[10.5px] uppercase tracking-[0.14em] font-medium rounded-full px-2 py-0.5"
              style={{
                background: "var(--v2-accent-soft, rgba(167,139,250,0.15))",
                color: "var(--v2-accent-strong, #5D50C8)",
              }}
            >
              {slug.replace(/_/g, " ")}
            </span>
          ))}
        </div>
      </div>

      <p
        className="mt-3 text-sm leading-relaxed"
        style={{ color: "var(--v2-text-muted, #A1A1AA)" }}
      >
        {truncateBio(pro.bio)}{" "}
        <span
          className="inline-flex items-center gap-0.5 font-semibold group-hover:underline"
          style={{ color: "var(--v2-accent-strong, #5D50C8)" }}
        >
          Read more
          <ArrowRight className="h-3 w-3" aria-hidden="true" />
        </span>
      </p>

      <div
        className="mt-4 pt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-xs"
        style={{
          borderTop: "1px solid var(--v2-border, rgba(255,255,255,0.08))",
          color: "var(--v2-text-muted, #A1A1AA)",
        }}
      >
        <span className="inline-flex items-center gap-1.5">
          <MapPin className="h-3 w-3" aria-hidden="true" /> {renderServiceArea(pro.service_area)}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Briefcase className="h-3 w-3" aria-hidden="true" /> {pro.years_experience} yrs experience
        </span>
        <span
          className="inline-flex items-center font-semibold tabular-nums"
          style={{ color: "var(--v2-accent-strong, #5D50C8)" }}
          aria-label={`Typical rate range ${rateRange}`}
        >
          {rateRange}
        </span>
      </div>
    </Link>
  );
}
